import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  isPossiblePhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

/**
 * Type guard to check if a string is a valid CountryCode.
 * Returns true if the value is a recognized two-letter ISO country code supported by libphonenumber-js.
 */
export const isCountryCode = (value: string): value is CountryCode => {
  const validCountries = getCountries();
  return validCountries.includes(value as CountryCode);
};

/**
 * Gets the numeric value from a formatted phone number including the country code
 */
export const getPhoneNumberValue = (formattedValue: string): string => {
  if (!formattedValue) return "";

  const phoneNumber = parsePhoneNumberFromString(formattedValue);
  if (phoneNumber) {
    return phoneNumber.number;
  }

  // Fallback to the original value if parsing fails
  return formattedValue;
};

/**
 * Validates if a phone number is valid
 */
export const isValidPhoneNumberValue = (value: string, countryCode?: CountryCode): boolean => {
  if (!value) return false;

  let numberToValidate = value.trim();

  // Handle DSN/military numbers
  const dsnMatch = numberToValidate.match(/^(\d{2})\+(.+)$/);
  if (dsnMatch) {
    // Validate the part after the DSN prefix
    const phonePartAfterDsn = dsnMatch[2];
    const phoneNumber = countryCode
      ? parsePhoneNumberFromString(phonePartAfterDsn, countryCode)
      : parsePhoneNumberFromString(phonePartAfterDsn);
    return phoneNumber ? phoneNumber.isValid() : false;
  }

  if (!countryCode) {
    if (!numberToValidate.startsWith("+") && /^\d/.test(numberToValidate)) {
      numberToValidate = "+" + numberToValidate;
    }
  }

  const phoneNumber = countryCode
    ? parsePhoneNumberFromString(numberToValidate, countryCode)
    : parsePhoneNumberFromString(numberToValidate);
  return phoneNumber ? phoneNumber.isValid() : false;
};

/**
 * Formats a phone number using libphonenumber-js
 */
export const formatPhoneNumber = (value: string, countryCode?: CountryCode): string => {
  if (!value) return "";

  const dsnMatch = value.match(/^(\d{2})\+/); // Detect and preserve DSN/military dialing prefixes like 94+ or 99+
  const dsnPrefix = dsnMatch ? dsnMatch[0] : "";
  const withoutDsn = dsnPrefix ? value.slice(dsnPrefix.length) : value;

  // Clean the input first (keep leading + and digits)
  const cleanValue = withoutDsn.replace(/[^+\d]/g, "");

  const parsed = countryCode
    ? parsePhoneNumberFromString(cleanValue, countryCode)
    : parsePhoneNumberFromString(cleanValue.startsWith("+") ? cleanValue : "+" + cleanValue);

  if (parsed && parsed.isValid()) {
    // When countryCode is provided, always show national format (without country code in display)
    const formatted = countryCode ? parsed.formatNational() : parsed.formatInternational();
    // Prepend DSN prefix if it was present
    return dsnPrefix ? dsnPrefix + formatted : formatted;
  }

  // Fallback to AsYouType for partial input
  const formatter = countryCode ? new AsYouType(countryCode) : new AsYouType();

  // When using AsYouType with a country hint, strip the country calling code prefix if present
  // to avoid displaying it redundantly (e.g., "1 (231)..." when US is already selected)
  let valueForFormatter = cleanValue;
  if (countryCode) {
    const callingCode = getCountryCallingCode(countryCode);
    const withoutPlus = cleanValue.startsWith("+") ? cleanValue.slice(1) : cleanValue;
    if (withoutPlus.startsWith(callingCode)) {
      valueForFormatter = withoutPlus.slice(callingCode.length);
    } else {
      valueForFormatter = withoutPlus;
    }
  }

  const formatted = formatter.input(valueForFormatter);
  // Prepend DSN prefix if it was present
  return dsnPrefix ? dsnPrefix + formatted : formatted;
};

/**
 * Returns the maximum possible count of national significant digits for a given country.
 * Uses libphonenumber-js "isPossiblePhoneNumber" which validates by general length rules (not precise patterns).
 * Falls back to E.164 max (15) if no country provided or no result.
 */
export const getMaxPossibleDigitsForCountry = (country?: CountryCode): number => {
  if (!country) return 15;
  // Search a reasonable range of national digits (min 3 up to 17) and find the last possible length
  let max = 3;
  for (let n = 3; n <= 17; n++) {
    const candidate = "9".repeat(n); // Use a string of nines as a generic number to test length
    if (isPossiblePhoneNumber(candidate, country)) {
      max = n;
    }
  }
  // Bound by E.164 max national significant digits (upper bound 15 is common, but national lengths vary).
  return Math.min(max, 15);
};

/**
 * Gets the country code from a phone number
 */
export const getCountryCode = (value: string): CountryCode | undefined => {
  if (!value) return undefined;

  const phoneNumber = parsePhoneNumberFromString(value);
  return phoneNumber?.country;
};

/**
 * Generates flag emoji from country code
 */
export const countryFlagEmoji = (code: string): string => {
  return code
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
};
